import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvpForm, setRsvpForm] = useState({ event_id: '', name: '', email: '' });
  const [rsvpStatus, setRsvpStatus] = useState({});

  useEffect(() => {
    axios.get(`${API}/events`).then(r => { if (Array.isArray(r.data)) setEvents(r.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleRsvp = async (eventId) => {
    if (!rsvpForm.name || !rsvpForm.email) return;
    try {
      const { data } = await axios.post(`${API}/events/rsvp`, { event_id: eventId, name: rsvpForm.name, email: rsvpForm.email });
      setRsvpStatus({ ...rsvpStatus, [eventId]: data.message });
      setRsvpForm({ event_id: '', name: '', email: '' });
      // Refresh
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (e) {
      setRsvpStatus({ ...rsvpStatus, [eventId]: e.response?.data?.detail || 'Failed' });
    }
  };

  const TYPE_LABELS = { meetup: 'Meetup', workshop: 'Workshop', info_session: 'Info Session' };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading events...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Events & Meetups — Info Sessions & Workshops"
        description="Join free info sessions, hands-on workshops and meetups for AI, ML & Data Science. Meet IoA USA instructors and graduates. Tri-state area (PA/NY/NJ) plus virtual events."
        path="/events"
      />
      <div className="max-w-5xl mx-auto px-6 py-24">
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Events & Meetups</p>
        <h1 data-testid="events-heading" className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Meet Us In Person.
        </h1>
        <p className="text-lg text-white/80 mb-16 max-w-2xl">Join our free info sessions, hands-on workshops, and community meetups. Meet instructors, network with graduates, and experience IoA USA&trade; firsthand.</p>

        <div className="space-y-6">
          {events.map((event, i) => (
            <div key={event.id || i} data-testid={`event-card-${i}`} className="border border-[#262626] bg-[#111111]">
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-xs font-mono font-bold px-2 py-1 bg-[#0077B6]/10 text-[#0077B6] mb-3 inline-block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{TYPE_LABELS[event.event_type] || 'Event'}</span>
                    <h2 className="text-xl font-bold tracking-tight text-white mt-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{event.title}</h2>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                    <p className="text-sm text-zinc-400">{event.time}</p>
                  </div>
                </div>
                <p className="text-base text-zinc-300 leading-relaxed mb-4">{event.description}</p>
                <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-6">
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} /> {event.location}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#0077B6]" strokeWidth={1.5} /> {event.rsvp_count || 0}/{event.max_attendees} spots</span>
                  {event.meetup_url && <a href={event.meetup_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#0077B6] hover:underline">View on Meetup.com</a>}
                </div>

                {/* RSVP Form */}
                {rsvpStatus[event.id] ? (
                  <div className="flex items-center gap-2 text-green-400 text-base">
                    <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} /> {rsvpStatus[event.id]}
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      data-testid={`rsvp-name-${i}`}
                      value={rsvpForm.event_id === event.id ? rsvpForm.name : ''}
                      onChange={e => setRsvpForm({ ...rsvpForm, event_id: event.id, name: e.target.value })}
                      className="px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500 flex-1"
                      placeholder="Your name"
                    />
                    <input
                      data-testid={`rsvp-email-${i}`}
                      type="email"
                      value={rsvpForm.event_id === event.id ? rsvpForm.email : ''}
                      onChange={e => setRsvpForm({ ...rsvpForm, event_id: event.id, email: e.target.value })}
                      className="px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500 flex-1"
                      placeholder="Your email"
                    />
                    <button
                      onClick={() => handleRsvp(event.id)}
                      data-testid={`rsvp-btn-${i}`}
                      className="brand-btn text-base px-6 py-3 whitespace-nowrap"
                    >
                      RSVP Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {events.length === 0 && (
            <div className="text-center py-16 border border-[#262626] bg-[#111111]">
              <Calendar className="w-16 h-16 text-zinc-400 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-zinc-300">No upcoming events. Check back soon!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
